import { useCallback, useEffect, useMemo, useRef } from 'react';
import tw, { styled } from 'twin.macro';
import { useAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';

import { EmployerRestaurantCard } from '@restworld/ui-ds';
import { useAnalytics, useKv } from '@restworld/utils-common';
import { useAuth } from '@restworld/feature-authentication';

import { RestaurantInfo } from '../hooks/use-employer-restaurants';
import { newlyCreatedRestaurantIdAtom } from '../restaurant-onboarding-modal/restaurant-onboarding-successful';

const Spotlight = dynamic(
  () => import('../../../../../components/motion-primitives/spotlight').then((mod) => mod.Spotlight),
  { ssr: false }
);

const GlowEffect = dynamic(
  () => import('../../../../../components/motion-primitives/glow-effect').then((mod) => mod.GlowEffect),
  { ssr: false }
);

function useEmployerRestaurantCards({ restaurants }: { restaurants?: RestaurantInfo[] }) {
  const [newlyCreatedRestaurantId, setNewlyCreatedRestaurantId] = useAtom(newlyCreatedRestaurantIdAtom);
  const { t: tC } = useTranslation('common');
  const { getKV, setKV } = useKv();
  const { trackEvent } = useAnalytics();
  const { user } = useAuth();

  const kvKey = useMemo(() => (user?.id ? `${user.id}-newly-created-venue-id` : ''), [user?.id]);

  const handleCardClick = useCallback(
    ({
      isNewlyCreated,
      resturantName,
      openJps,
    }: {
      isNewlyCreated: boolean;
      resturantName: string;
      openJps: number;
    }) => {
      if (isNewlyCreated && kvKey) {
        setKV({ key: kvKey, value: undefined, type: 'employer_newly_created_venue_id' });
        setNewlyCreatedRestaurantId('');
      }
      trackEvent('employer_view_locali_details', {
        restaurant_name: resturantName,
        open_jps: openJps,
        email: user?.email,
      });
    },
    [kvKey, setKV, setNewlyCreatedRestaurantId, trackEvent, user?.email]
  );

  useEffect(() => {
    if (!kvKey) return;

    getKV(kvKey)
      .then((data) => {
        setNewlyCreatedRestaurantId(data?.value);
      })
      .catch((e) => console.error(e));
  }, [getKV, kvKey, setNewlyCreatedRestaurantId]);

  const restaurantCards = useMemo(
    () =>
      restaurants?.map(
        ({
          notViewedNormalJobApplicantsAvatar,
          notViewedSpontaneousApplicantsAvatar,
          notViewedNormalJobApplicationCount,
          notViewedSpontaneousApplicationCount,
          restaurant: {
            id,
            address,
            active_job_positions,
            pictures,
            restaurant_logo,
            restaurant_name,
            types,
            slug,
            show_details,
          },
        }) => {
          const candidatesCount =
            (notViewedNormalJobApplicationCount || 0) + (notViewedSpontaneousApplicationCount || 0);

          const applicants = [
            ...(notViewedNormalJobApplicantsAvatar || []),
            ...(notViewedSpontaneousApplicantsAvatar || []),
          ];

          const employerCandidates = applicants.map((candidate) => ({
            firstName: candidate.name,
            lastName: candidate.surname,
            image: candidate.avatar_url,
          }));

          const isAllSpontaneous = !!notViewedSpontaneousApplicationCount && !notViewedNormalJobApplicationCount;

          const isNewlyCreated = id === newlyCreatedRestaurantId;

          let candidatesDisplayMessage: string;
          if (isAllSpontaneous) candidatesDisplayMessage = tC('spontaneous_applications');
          else if (candidatesCount === 1) candidatesDisplayMessage = tC('new_candidate_to_view');
          else candidatesDisplayMessage = tC('candidates_to_view');

          const restaurantCardProps = {
            restaurantName: restaurant_name,
            restaurantLogo: restaurant_logo,
            address,
            openJpsCount: active_job_positions?.length,
            pictures,
            types,
            href: `/employer/venues/${slug}`,
            candidates: {
              count: candidatesCount,
              candidates: employerCandidates,
              message: candidatesDisplayMessage,
              isAllSpontaneous,
            },
            onClick: () =>
              handleCardClick({
                isNewlyCreated,
                resturantName: restaurant_name,
                openJps: active_job_positions?.length,
              }),
            isShowcaseOffline: !show_details,
            isHighlighted: isNewlyCreated,
          };

          const card = <EmployerRestaurantCard key={id} {...restaurantCardProps} />;

          if (isNewlyCreated) {
            return (
              <NewlyCreatedCardWrapper key={id}>
                <HighlightWrapper>
                  <GlowEffect
                    colors={['#f0ebf8', '#9876d1', '#fae6e9', '#b9a1e0']}
                    mode="flowHorizontal"
                    blur={8}
                    duration={5}
                    scale={0.99}
                  />
                  <HighlightBorder>
                    <Spotlight
                      gradient="radial-gradient(circle at center, #6633bb, #855cc9, #9876d1, transparent 80%)"
                      size={700}
                      springOptions={{ stiffness: 300, damping: 30 }}
                    />
                    <CardInnerWrapper>{card}</CardInnerWrapper>
                  </HighlightBorder>
                </HighlightWrapper>
              </NewlyCreatedCardWrapper>
            );
          }

          return <div key={id}>{card}</div>;
        }
      ),
    [restaurants, newlyCreatedRestaurantId, tC, handleCardClick]
  );

  return restaurantCards;
}

export default useEmployerRestaurantCards;

const HighlightWrapper = styled.div`
  ${tw`relative rounded-2xl`}
`;
const HighlightBorder = styled.div`
  ${tw`relative overflow-hidden rounded-2xl bg-purple-50 p-px`}
`;
const CardInnerWrapper = styled.div`
  ${tw`relative overflow-hidden rounded-2xl bg-neutral-10`}
`;

function NewlyCreatedCardWrapper({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current) {
      setTimeout(() => {
        ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
    }
  }, []);

  return <div ref={ref}>{children}</div>;
}
